import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  XCircle,
  type LucideIcon,
} from 'lucide-react'

import { cn } from '@/lib/utils'

const alertVariants = cva(
  [
    'relative w-full rounded-lg border text-sm',
    'flex items-start gap-3 px-4 py-3',
    '[&_[data-slot=alert-icon]]:mt-0.5 [&_[data-slot=alert-icon]]:size-4 [&_[data-slot=alert-icon]]:shrink-0',
  ].join(' '),
  {
    variants: {
      tone: {
        info: 'border-info/30 bg-info-soft text-info-soft-foreground',
        success: 'border-success/30 bg-success-soft text-success-soft-foreground',
        warning: 'border-warning/40 bg-warning-soft text-warning-soft-foreground',
        danger:
          'border-destructive/40 bg-destructive-soft text-destructive-soft-foreground',
        neutral: 'border-border bg-muted text-foreground',
      },
      variant: {
        soft: '',
        outline: 'bg-card',
      },
    },
    compoundVariants: [
      { variant: 'outline', tone: 'info', className: 'bg-card text-foreground' },
      { variant: 'outline', tone: 'success', className: 'bg-card text-foreground' },
      { variant: 'outline', tone: 'warning', className: 'bg-card text-foreground' },
      { variant: 'outline', tone: 'danger', className: 'bg-card text-foreground' },
      { variant: 'outline', tone: 'neutral', className: 'bg-card text-foreground' },
    ],
    defaultVariants: {
      tone: 'info',
      variant: 'soft',
    },
  },
)

const TONE_ICONS: Record<NonNullable<VariantProps<typeof alertVariants>['tone']>, LucideIcon> = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: XCircle,
  neutral: Info,
}

type AlertElementProps = Omit<React.ComponentProps<'div'>, 'role'>

interface AlertProps extends AlertElementProps, VariantProps<typeof alertVariants> {
  /**
   * Leading icon. Defaults to the per-tone Lucide icon. Pass `false` to
   * suppress, or pass a `LucideIcon` to override.
   */
  icon?: LucideIcon | false
  /** Optional element rendered after the body — used for inline actions / dismiss buttons. */
  action?: React.ReactNode
  /**
   * Explicit ARIA role override. Defaults to `alert` for `danger`/`warning`
   * and `status` otherwise so screen readers don't interrupt for benign info.
   */
  role?: 'alert' | 'status' | 'none'
}

function defaultRoleForTone(
  tone: AlertProps['tone'] | undefined,
): NonNullable<AlertProps['role']> {
  if (tone === 'danger' || tone === 'warning') return 'alert'
  return 'status'
}

function Alert({
  className,
  tone,
  variant,
  icon,
  role,
  action,
  children,
  ...props
}: AlertProps) {
  const resolvedTone = tone ?? 'info'
  const resolvedRole = role ?? defaultRoleForTone(resolvedTone)
  const IconCmp = icon === false ? null : (icon ?? TONE_ICONS[resolvedTone])

  return (
    <div
      data-slot="alert"
      data-tone={resolvedTone}
      data-variant={variant ?? 'soft'}
      role={resolvedRole}
      className={cn(alertVariants({ tone, variant, className }))}
      {...props}
    >
      {IconCmp ? <IconCmp data-slot="alert-icon" aria-hidden /> : null}
      <div className="min-w-0 flex-1 space-y-1">{children}</div>
      {action ? (
        <div data-slot="alert-action" className="ml-2 flex shrink-0 items-center gap-1">
          {action}
        </div>
      ) : null}
    </div>
  )
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        'font-semibold leading-tight text-inherit',
        className,
      )}
      {...props}
    />
  )
}

function AlertDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        'text-sm leading-relaxed text-inherit/90 [&_p]:leading-relaxed',
        className,
      )}
      {...props}
    />
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export { Alert, AlertTitle, AlertDescription, alertVariants }
